import {
  SECTION_TYPE_ICON,
  resolveBriefSectionType,
  type BriefSectionType,
} from '../../lib/sectionTypes';

interface BriefSectionTypeIconProps {
  type: BriefSectionType;
  className?: string;
  /**
   * Names the type (e.g. "Text", "Check list"). Supplying it makes the icon the thing that
   * CARRIES the type rather than decoration, so it becomes a named image instead of hidden.
   * Omit it where the surrounding copy already says the type.
   */
  label?: string;
  /** Lets a consumer query the rendered icon directly (e.g. a row with more than one icon). */
  'data-testid'?: string;
}

/** Shared by the type picker, the section row, and the preview panel. */
export function BriefSectionTypeIcon(props: BriefSectionTypeIconProps) {
  // `toSectionDraft` already resolves an absent/unrecognized server `type` to `'text'`, but this
  // component is shared (type picker, row, preview) and defends its own prop independently
  // rather than trusting every caller to have gone through that boundary — falling back to the
  // text icon, the same default `resolveBriefSectionType` uses, rather than rendering `undefined`.
  //
  // Same tooling/type-resolution gap documented on `BRIEF_SECTION_TYPES` in `sectionTypes.ts`:
  // a lucide-react icon component resolves as an error type under this project's current
  // typescript-eslint type resolution, even though `SECTION_TYPE_ICON` is explicitly typed as
  // `Record<BriefSectionType, LucideIcon>`.

  const Icon = SECTION_TYPE_ICON[resolveBriefSectionType(props.type)];

  // A labelled icon is named for assistive tech and gets the hover tooltip from an SVG
  // `<title>` child — lucide forwards children but has no `title` PROP, and passing one put a
  // string where `LucideProps` has no such key. An unlabelled icon stays decorative.
  if (props.label !== undefined) {
    return (
      <Icon
        role="img"
        aria-label={props.label}
        className={props.className ?? 'size-4'}
        data-testid={props['data-testid']}
      >
        <title>{props.label}</title>
      </Icon>
    );
  }

  return (
    <Icon aria-hidden className={props.className ?? 'size-4'} data-testid={props['data-testid']} />
  );
}
